import { useTranslation } from "react-i18next";
import { format_num } from "../utils/format/format_num";
import { useUserData } from "../hooks/useUserData";
import { cn } from "../ui/cn";


const SHELL_ICON = "/ui/shell-icon.png";
const TON_ICON = "/ui/ton-icon.svg";
const USDT_ICON = "/ui/usdt-icon.svg";

interface PearlsBalanceProps {
  /** "dark" = white text on dark bg, "light" = dark text on light bg */
  variant?: "dark" | "light";
  /** Override outer padding, e.g. "8px 20px 0 20px" */
  padding?: string;
  /** Short label format: "Balance:" instead of "Your balance:" */
  compact?: boolean;
  /** Hide TON/USDT crypto balances, show only pearls */
  hideCrypto?: boolean;
  /** Override styles on the outer container */
  style?: React.CSSProperties;
  /** Override styles on the value <span> */
  valueStyle?: React.CSSProperties;
}

export default function PearlsBalance({
  variant = "dark",
  padding,
  compact = false,
  hideCrypto = false,
  style,
  valueStyle,
}: PearlsBalanceProps) {
  const { t } = useTranslation();
  const { data: userData } = useUserData();

  if (!userData) return null;

  const isDark = variant === "dark";
  const hasCrypto = !hideCrypto && ((userData.balance_ton ?? 0) > 0 || (userData.balance_usdt ?? 0) > 0);

  return (
    <div
      className={cn(
        "flex flex-col gap-xs [font-family:Lato,sans-serif] text-[14px] font-semibold",
        isDark ? "text-white-alpha-55" : "text-text-dim",
      )}
      style={{
        padding: padding ?? "0", // dynamic prop
        ...style, // dynamic prop override
      }}
    >
      <div className="flex items-center gap-sm">
        {compact ? t("Balance") : t("Your balance")}:&nbsp;
        <span
          className={cn(
            "font-bold",
            isDark ? "text-white" : "text-fullscreen-surface",
          )}
          style={valueStyle}
        >
          {format_num(userData.currency_balance ?? 0)}
        </span>
        <img
          src={SHELL_ICON}
          alt="Pearls"
          className="h-4 w-5"
        />
      </div>
      {hasCrypto && (
        <div className="flex items-center gap-[10px] text-[12px]">
          {(userData.balance_ton ?? 0) > 0 && (
            <span className="flex items-center gap-[3px] font-bold text-ton-label">
              <img src={TON_ICON} alt="TON" className="h-[14px] w-[14px]" />
              {(userData.balance_ton ?? 0).toFixed(4)}
            </span>
          )}
          {(userData.balance_usdt ?? 0) > 0 && (
            <span className="flex items-center gap-[3px] font-bold text-usdt-label">
              <img src={USDT_ICON} alt="USDT" className="h-[14px] w-[14px]" />
              ${(userData.balance_usdt ?? 0).toFixed(2)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
